import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { openAuthedFile } from '@/lib/fileDownload';
import api, { updateStakeholderTag, deleteStakeholderTag } from '@/lib/api';
import { FINANCIAL_ROLES, canSeeFinancials as userCanSeeFinancials, canEditOrders, canPrintJobCard, canCompleteOrders } from '@/lib/constants';
import { AlertTriangle } from 'lucide-react';
import { DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import InvolvedPeopleSection from '@/components/InvolvedPeopleSection';
import AddStakeholderDialog from '@/components/AddStakeholderDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import OrderFactsBand from './OrderDetail/OrderFactsBand';
import AuditTimeline from './OrderDetail/AuditTimeline';
import OrderHeader from './OrderDetail/OrderHeader';
import LineItemsSection from './OrderDetail/LineItemsSection';
import PaymentSidebar from './OrderDetail/PaymentSidebar';

import { Screen } from '@/components/data';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMode, setPayMode] = useState('');
  const [payRef, setPayRef] = useState('');


  const [addStakeholderOpen, setAddStakeholderOpen] = useState(false);
  const [editStakeholderOpen, setEditStakeholderOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editRoleNote, setEditRoleNote] = useState('');
  const [stakeholderToRemove, setStakeholderToRemove] = useState(null);

  const canSeeFinancials = userCanSeeFinancials(user);
  const canEditOrder = canEditOrders(user);
  const canRecordPayment = FINANCIAL_ROLES.includes(user?.role);
  const PLATE_ROLES = ['admin', 'designer', 'supervisor'];
  const canManagePlate = PLATE_ROLES.includes(user?.role);

  const [plateOrderOpen, setPlateOrderOpen] = useState(false);
  const [plateOrderItem, setPlateOrderItem] = useState(null);
  const [plateVendor, setPlateVendor] = useState('');
  const [plateSubmitting, setPlateSubmitting] = useState(false);

  const handlePlateOrder = async () => {
    if (!plateOrderItem?.job_card_id) return;
    setPlateSubmitting(true);
    try {
      // the server's own sentence: sending a plate out also closes the design,
      // and a hard-coded toast could never say so
      const marked = await api.post(`/plates/${plateOrderItem.job_card_id}/mark-ordered`, {});
      toast.success(marked?.data?.message || 'Plate marked as ordered');
      setPlateOrderOpen(false);
      setPlateVendor('');
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    setPlateSubmitting(false);
  };

  const handlePlateReceive = async (item) => {
    if (!item?.job_card_id) return;
    try {
      const marked = await api.post(`/plates/${item.job_card_id}/mark-received`, {});
      toast.success(marked?.data?.message || 'Plate marked as received');
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const [moveBackOpen, setMoveBackOpen] = useState(false);
  const [moveBackTarget, setMoveBackTarget] = useState(null);
  const [moveBackReason, setMoveBackReason] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);
  const canMoveStages = ['admin', 'supervisor'].includes(user?.role);

  const [expandedBatches, setExpandedBatches] = useState({});
  const toggleBatchHistory = (batchId) => {
    setExpandedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const [lineItemsSortOrder, setLineItemsSortOrder] = useState('none');
  const toggleLineItemsSort = () => {
    setLineItemsSortOrder(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none');
  };

  const handleMoveForward = async (jobCardId, batchId) => {
    setMoveLoading(true);
    try {
      const body = batchId ? { batch_id: batchId } : {};
      const res = await api.post(`/job-cards/${jobCardId}/move-forward`, body);
      toast.success(res.data?.message || 'Moved forward');
      const orderRes = await api.get(`/orders/${id}`);
      setOrder(orderRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot move forward');
    }
    setMoveLoading(false);
  };

  const handleMoveBackwardConfirm = async () => {
    if (!moveBackTarget) return;
    setMoveLoading(true);
    try {
      const body = { reason: moveBackReason || undefined };
      if (moveBackTarget.batchId) body.batch_id = moveBackTarget.batchId;
      const res = await api.post(`/job-cards/${moveBackTarget.jobCardId}/move-backward`, body);
      toast.success(res.data?.message || 'Moved backward');
      setMoveBackOpen(false);
      setMoveBackReason('');
      setMoveBackTarget(null);
      const orderRes = await api.get(`/orders/${id}`);
      setOrder(orderRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot move backward');
    }
    setMoveLoading(false);
  };

  const [completeOrderOpen, setCompleteOrderOpen] = useState(false);
  const [deleteOrderOpen, setDeleteOrderOpen] = useState(false);
  const [deleteLineItemOpen, setDeleteLineItemOpen] = useState(false);
  const [lineItemToDelete, setLineItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isOrderInProduction = order?.line_items?.some(item =>
    item.status === 'in_production' ||
    (item.batches && item.batches.some(b => (b.current_stage_index || 0) > 0))
  );

  const handleDeleteOrder = async () => {
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/orders/${order.id}`);
      toast.success(res.data?.message || 'Order deleted successfully');
      navigate('/orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete order');
    }
    setDeleteLoading(false);
    setDeleteOrderOpen(false);
  };

  // Everything still open on this order is closed at once and the goods
  // recorded as delivered — how work that was finished months ago gets onto the
  // books without walking it through the floor a second time.
  const handleCompleteOrder = async () => {
    setCompleteLoading(true);
    try {
      const res = await api.post(`/orders/${order.id}/complete`);
      toast.success(res.data?.message || 'Order marked as delivered');
      const orderRes = await api.get(`/orders/${id}`);
      setOrder(orderRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete order');
    }
    setCompleteLoading(false);
    setCompleteOrderOpen(false);
  };

  const handleDeleteLineItem = async () => {
    if (!lineItemToDelete) return;
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/orders/${order.id}/line-items/${lineItemToDelete.id}`);
      toast.success(res.data?.message || 'Line item deleted');
      const orderRes = await api.get(`/orders/${id}`);
      setOrder(orderRes.data);
      if (res.data?.order_is_empty) {
        toast.warning('This order has no more items. Add items or delete the order.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete line item');
    }
    setDeleteLoading(false);
    setDeleteLineItemOpen(false);
    setLineItemToDelete(null);
  };

  const openDeleteLineItemDialog = (item) => {
    setLineItemToDelete(item);
    setDeleteLineItemOpen(true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/orders/${id}`);
        setOrder(res.data);
      } catch {
        toast.error('Failed to load order');
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const handlePrintJobCard = async () => {
    if (!order?.order_id) {
      toast.error('Order ID not found');
      return;
    }
    try {
      // the A4 form is the sheet the floor works from, the same one the job card
      // screen prints — this one carries every line item on the order
      await openAuthedFile(`/jobcard/${order.order_id}/forms.pdf`);
    } catch (e) {
      // 400 is the one the desk hits: no line item on the order has a job card
      // yet, so there is nothing to print a form for
      const status = e?.response?.status;
      if (status === 403) toast.error('You cannot print job cards');
      else if (status === 400) toast.error('No line item on this order has a job card yet');
      else toast.error('Could not open the job cards');
    }
  };

  const recordPayment = async () => {
    if (!payAmount || !payMode) { toast.error('Enter amount and mode'); return; }
    try {
      await api.post(`/orders/${order.id}/payments`, {
        amount: parseFloat(payAmount), mode: payMode, reference_number: payRef,
      });
      toast.success('Payment recorded');
      setPayOpen(false);
      setPayAmount(''); setPayMode(''); setPayRef('');
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleAddStakeholder = () => {
    setAddStakeholderOpen(true);
  };

  const handleAddStakeholderSuccess = async () => {
    try {
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (err) {
    }
  };

  const handleEditStakeholder = (tag) => {
    setEditingTag(tag);
    setEditRoleNote(tag.role_note || '');
    setEditStakeholderOpen(true);
  };

  const handleUpdateStakeholder = async () => {
    if (!editingTag) return;

    try {
      await updateStakeholderTag(order.id, editingTag.id, {
        role_note: editRoleNote,
      });
      toast.success('Role note updated successfully');
      setEditStakeholderOpen(false);
      setEditingTag(null);
      setEditRoleNote('');

      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      const errorDetail = error.response?.data?.detail;

      if (errorCode === 'ROLE_NOTE_TOO_LONG') {
        toast.error(errorDetail || 'Role note must be 100 characters or less');
      } else if (errorCode === 'TAG_NOT_FOUND') {
        toast.error(errorDetail || 'Stakeholder tag not found');
      } else if (errorCode === 'CANNOT_EDIT_TAG') {
        toast.error(errorDetail || 'You can only edit tags you created');
      } else {
        toast.error('Failed to update role note. Please try again.');
      }
    }
  };

  const handleRemoveStakeholder = async (tagId) => {
    setStakeholderToRemove(tagId);
  };

  const confirmRemoveStakeholder = async () => {
    const tagId = stakeholderToRemove;
    setStakeholderToRemove(null);
    if (!tagId) return;

    try {
      await deleteStakeholderTag(order.id, tagId);
      toast.success('Person removed successfully');

      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      const errorDetail = error.response?.data?.detail;

      if (errorCode === 'TAG_NOT_FOUND') {
        toast.error(errorDetail || 'Stakeholder tag not found');
      } else if (errorCode === 'CANNOT_EDIT_TAG') {
        toast.error(errorDetail || 'You can only remove tags you created');
      } else {
        toast.error('Failed to remove person. Please try again.');
      }
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" /></div>;
  if (!order) return <div className="text-center py-12 text-slate-500">Order not found</div>;

  return (
    <Screen data-testid="order-detail-page">
      <OrderHeader
        order={order}
        isAdmin={isAdmin}
        canEdit={canEditOrder}
        canPrint={canPrintJobCard(user)}
        onBack={() => navigate('/orders')}
        onEditOrder={() => navigate(`/orders/${order.id}/edit`)}
        canComplete={canCompleteOrders(user) && !['delivered', 'stocked'].includes(order.status)}
        onDeleteOrder={() => setDeleteOrderOpen(true)}
        onCompleteOrder={() => setCompleteOrderOpen(true)}
        onPrintJobCard={handlePrintJobCard}
      />

      <OrderFactsBand order={order} canSeeFinancials={canSeeFinancials} />

      {/* Items take the height that is left; the rail beside them scrolls on
          its own, so the page itself never does. */}
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-h-0 overflow-y-auto pr-1">
          <LineItemsSection
            order={order}
            lineItemsSortOrder={lineItemsSortOrder}
            toggleLineItemsSort={toggleLineItemsSort}
            canSeeFinancials={canSeeFinancials}
            canMoveStages={canMoveStages}
            handleMoveForward={handleMoveForward}
            moveLoading={moveLoading}
            onMoveBack={(target) => {
              setMoveBackTarget(target);
              setMoveBackOpen(true);
            }}
            isAdmin={isAdmin}
            onDeleteLineItem={openDeleteLineItemDialog}
            expandedBatches={expandedBatches}
            toggleBatchHistory={toggleBatchHistory}
          />
        </div>

        <div className="min-h-0 space-y-3 overflow-y-auto pr-1">
          <InvolvedPeopleSection
            order={order}
            onAddStakeholder={handleAddStakeholder}
            onEditStakeholder={handleEditStakeholder}
            onRemoveStakeholder={handleRemoveStakeholder}
            currentUser={user}
          />

          {canSeeFinancials && (
            <PaymentSidebar
              order={order}
              canRecordPayment={canRecordPayment}
              payOpen={payOpen}
              setPayOpen={setPayOpen}
              payAmount={payAmount}
              setPayAmount={setPayAmount}
              payMode={payMode}
              setPayMode={setPayMode}
              payRef={payRef}
              setPayRef={setPayRef}
              recordPayment={recordPayment}
            />
          )}

          <AuditTimeline auditLogs={order.audit_logs} />
        </div>
      </div>

      {/* Add Stakeholder Dialog */}
      <AddStakeholderDialog
        open={addStakeholderOpen}
        onOpenChange={setAddStakeholderOpen}
        orderId={order?.id}
        onSuccess={handleAddStakeholderSuccess}
      />

      {/* Edit Stakeholder Dialog */}
      <Dialog open={editStakeholderOpen} onOpenChange={setEditStakeholderOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Role Note</DialogTitle>
            <DialogDescription>
              Update the role description for {editingTag?.user_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Role Note
            </label>
            <Textarea
              value={editRoleNote}
              onChange={(e) => setEditRoleNote(e.target.value)}
              placeholder="e.g., gave pricing approval, coordinated with customer"
              rows={3}
              maxLength={100}
              className="resize-none"
            />
            <p className="text-xs text-slate-500 mt-1">
              {editRoleNote.length}/100 characters
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditStakeholderOpen(false);
                setEditingTag(null);
                setEditRoleNote('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateStakeholder}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plate Order Dialog */}
      <Dialog open={plateOrderOpen} onOpenChange={setPlateOrderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Plate as Ordered</DialogTitle>
            <DialogDescription>Record the vendor details for the plate order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Vendor Name</label>
              <Input
                placeholder="Enter plate vendor name"
                value={plateVendor}
                onChange={e => setPlateVendor(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPlateOrderOpen(false); setPlateVendor(''); }}>Cancel</Button>
            <Button onClick={handlePlateOrder} disabled={plateSubmitting || !plateVendor.trim()} className="bg-amber-600 hover:bg-amber-700 text-white">
              {plateSubmitting ? 'Saving...' : 'Mark as Ordered'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Backward Confirmation Dialog */}
      <Dialog open={moveBackOpen} onOpenChange={(v) => { setMoveBackOpen(v); if (!v) { setMoveBackReason(''); setMoveBackTarget(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Job Card Backward</DialogTitle>
            <DialogDescription>
              Are you sure you want to move this job card back to <strong>{moveBackTarget?.toStage}</strong>?
              {moveBackTarget?.fromStage && <span> (currently at {moveBackTarget.fromStage})</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm text-slate-600">Reason (optional):</label>
            <Textarea
              value={moveBackReason}
              onChange={(e) => setMoveBackReason(e.target.value)}
              placeholder="E.g., quality issue requires rework..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveBackOpen(false)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={moveLoading}
              onClick={handleMoveBackwardConfirm}
            >
              {moveLoading ? 'Moving...' : `Move back to ${moveBackTarget?.toStage}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!stakeholderToRemove} onOpenChange={() => setStakeholderToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this person from the order?</AlertDialogTitle>
            <AlertDialogDescription>
              They stop being listed as involved. Nothing they did to the order is undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveStakeholder}
              className="bg-red-600 text-white hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close-out Confirmation Dialog */}
      <AlertDialog open={completeOrderOpen} onOpenChange={setCompleteOrderOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Order #{order.order_id} as delivered?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Every stage still open is closed and all {order.line_items?.length || 0} item(s)
                recorded as delivered. Nothing is sent to the floor — use this for work that was
                finished long ago.
              </p>
              <p className="text-slate-500">
                Undoing it means moving the batches back by hand, one stage at a time.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCompleteOrder(); }}
              disabled={completeLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-complete-order-btn"
            >
              {completeLoading ? 'Completing...' : 'Mark Delivered'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Order Confirmation Dialog */}
      <AlertDialog open={deleteOrderOpen} onOpenChange={setDeleteOrderOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {isOrderInProduction && <AlertTriangle className="h-5 w-5 text-amber-500" />}
              Delete Order #{order.order_id}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {isOrderInProduction && (
                <p className="text-amber-600 font-medium">
                  \u26A0\uFE0F This order has items in production. Deleting it will remove them from the production queue.
                </p>
              )}
              <p>
                This will remove all {order.line_items?.length || 0} line item(s) and their job cards.
              </p>
              <p className="text-slate-500">
                This can be undone from the Recycle Bin.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrder}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteLoading ? 'Deleting...' : 'Delete Order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Line Item Confirmation Dialog */}
      <AlertDialog open={deleteLineItemOpen} onOpenChange={(v) => { setDeleteLineItemOpen(v); if (!v) setLineItemToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Line Item?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete <strong>{lineItemToDelete?.item_name || 'this item'}</strong>?
              </p>
              <p className="text-slate-500">
                Its job card will also be removed. This can be undone from the Recycle Bin.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLineItem}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteLoading ? 'Deleting...' : 'Delete Line Item'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
